import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MobileButton, Row, Text } from '@/components/ui';
import type { DialogConfig, DialogContextValue } from './dialogTypes';

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

interface DialogProviderProps {
  children: ReactNode;
}

let dialogIdCounter = 0;

export function DialogProvider({ children }: DialogProviderProps) {
  const [dialogs, setDialogs] = useState<DialogConfig[]>([]);
  const { t } = useTranslation();

  const showDialog = useCallback((config: Omit<DialogConfig, 'id'>): string => {
    const id = `dialog-${++dialogIdCounter}`;
    const dialog: DialogConfig = {
      ...config,
      id,
      type: config.type || 'confirm',
    };

    setDialogs(prev => [...prev, dialog]);
    return id;
  }, []);

  const hideDialog = useCallback((id: string) => {
    setDialogs(prev => prev.filter(dialog => dialog.id !== id));
  }, []);

  const hideAllDialogs = useCallback(() => {
    setDialogs([]);
  }, []);

  const handleConfirm = async (dialog: DialogConfig) => {
    try {
      if (dialog.onConfirm) {
        await dialog.onConfirm();
      }
    } finally {
      hideDialog(dialog.id);
    }
  };

  const handleCancel = (dialog: DialogConfig) => {
    if (dialog.onCancel) {
      dialog.onCancel();
    }
    hideDialog(dialog.id);
  };

  const value: DialogContextValue = {
    dialogs,
    showDialog,
    hideDialog,
    hideAllDialogs,
  };

  return (
    <DialogContext.Provider value={value}>
      {children}

      {/* Render dialogs */}
      {dialogs.map(dialog => (
        <Box
          key={dialog.id}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.2)',
            display: 'block',
            zIndex: 1300, // Above other components but below toasts
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: '20rem', // 320px
              maxWidth: '90vw',
              height: 'fit-content',
              maxHeight: '80vh',
              margin: 'auto',
              bottom: 0,
              top: 0,
              left: 0,
              right: 0,
              background: 'white',
              padding: '1rem', // 16px
              border: '1px solid #1C5B9C',
              borderRadius: '1rem', // 16px
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem', // 16px
            }}
          >
            {dialog.type === 'custom' && dialog.customComponent ? (
              <dialog.customComponent onClose={() => hideDialog(dialog.id)} />
            ) : (
              <>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    sx={{
                      color: 'black',
                      textAlign: 'center',
                      fontSize: '0.875rem', // 14px
                      lineHeight: 1.5,
                    }}
                  >
                    {dialog.title}
                    {dialog.message && (
                      <>
                        <br />
                        <Text
                          component='span'
                          sx={{ fontSize: '0.75rem', color: 'text.secondary' }} // 12px
                        >
                          {dialog.message}
                        </Text>
                      </>
                    )}
                  </Text>
                </Box>

                <Row spacing='1rem' alignItems='center' sx={{ flexShrink: 0 }}>
                  {' '}
                  {/* 16px */}
                  {dialog.type !== 'alert' && (
                    <MobileButton
                      sx={{ width: '50%' }}
                      btnColor='info.main'
                      onClick={() => handleCancel(dialog)}
                    >
                      <Text sx={{ color: 'black' }}>
                        {dialog.cancelText || t('cancel_confirm_modal')}
                      </Text>
                    </MobileButton>
                  )}
                  <MobileButton
                    sx={{ width: dialog.type === 'alert' ? '100%' : '50%' }}
                    btnColor='#1C5B9C'
                    onClick={() => handleConfirm(dialog)}
                  >
                    <Text>
                      {dialog.confirmText ||
                        (dialog.type === 'alert'
                          ? t('ok')
                          : t('delete_confirm_modal'))}
                    </Text>
                  </MobileButton>
                </Row>
              </>
            )}
          </Box>
        </Box>
      ))}
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogContextValue {
  const context = useContext(DialogContext);

  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogProvider');
  }

  return context;
}
